// Sprint usage-single-emitter (D013-EC-166) D3 — "how old is this
// number?", rendered wherever runner capacity is shown.
//
// Why it exists: after this sprint almost every usage surface reads the
// control-plane cache and deliberately never calls Anthropic. That is the
// right trade (spec §1) but it makes silence ambiguous — the founder
// cannot tell a live reading from one taken forty minutes ago. This
// caption removes the ambiguity, and over 30 minutes it says so plainly
// instead of implying freshness.
//
// New component (verified no equivalent exists): `lib/time.ts`
// `formatRelativeTime` renders a ≤3-char sidebar stamp ("12m") with no
// i18n and no staleness verdict — a different job on a different surface.
//
// No new design tokens: the existing caption role + muted foreground,
// the same pair every secondary line in the usage surfaces already uses.

import { useTranslation } from '../../i18n';
import type { RunnerUsageEntry } from '../../lib/api';

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

// Beyond this the surface stops implying freshness and says the reading
// may be out of date. 30 min = 2× the warmer's 15 min cadence (EC-140):
// one missed tick is normal, two means something is off.
export const USAGE_AGE_STALE_AFTER_MS = 30 * MINUTE_MS;

// Sprint usage-last-known-good (D013-EC-172) D5 — past this age a surface
// stops LEADING with the numbers: the row leads with the failure line and
// the numbers render muted.
//
// Justified on its own terms: 2× the 30 min caption threshold above. It is
// NOT "the same threshold as the scheduler" — `runner-router`'s bound is a
// scheduling decision about which input to trust, not a rendering decision
// about what to lead with, and the two are free to diverge. They happen to
// carry the same number today; nothing may derive one from the other.
//
// Pre-mortem F2: this is what stops a runner dead for hours from looking
// healthy forever behind a caption nobody reads.
export const USAGE_UI_STALE_AFTER_MS = 60 * MINUTE_MS;

export function usageAgeMs(
  atIso: string | null | undefined,
  now: number = Date.now(),
): number | null {
  if (!atIso) return null;
  const t = Date.parse(atIso);
  if (!Number.isFinite(t)) return null;
  // Clock skew between the founder's browser and the control plane can
  // make a fresh timestamp look like it is in the future. Clamp at 0
  // rather than rendering a negative age (pre-mortem F5).
  return Math.max(0, now - t);
}

// Compact, founder-readable duration: "8 min", "2 h", "1 d". Exported
// (sprint usage-from-stream) so `UsageModal.tsx`'s passed-reset copy
// (`usage.noReadingSinceReset`) can state "última hace X" without a
// second duration formatter (P6: reuse before creating).
export function coarseAge(ageMs: number): string {
  if (ageMs < HOUR_MS) return `${Math.floor(ageMs / MINUTE_MS)} min`;
  if (ageMs < 24 * HOUR_MS) return `${Math.floor(ageMs / HOUR_MS)} h`;
  return `${Math.floor(ageMs / (24 * HOUR_MS))} d`;
}

// Exported for the component tests — pure, so the copy can be asserted
// from a fixed ISO input without mounting a tree (pre-mortem F5).
export function usageAgeKey(
  ageMs: number | null,
): { key: 'ageUnknown' } | { key: 'ageJustNow' } | { key: 'ageMinutes' | 'ageStale'; age: string } {
  if (ageMs === null) return { key: 'ageUnknown' };
  if (ageMs < MINUTE_MS) return { key: 'ageJustNow' };
  if (ageMs > USAGE_AGE_STALE_AFTER_MS) return { key: 'ageStale', age: coarseAge(ageMs) };
  return { key: 'ageMinutes', age: coarseAge(ageMs) };
}

// Sprint usage-last-known-good (D013-EC-172) D3 — is this reading past the
// point where the surface should stop leading with it? Exported so the
// three founder-facing surfaces share ONE definition of "too old to lead
// with" instead of each re-deriving the comparison.
export function isUsageReadingStale(
  atIso: string | null | undefined,
  now: number = Date.now(),
): boolean {
  // ABSENT stamp: NOT treated as stale, and that is a factual call rather
  // than a lenient one. Post-D1 a row can never carry numbers without a
  // stamp — `doFetch` writes both together on every path. So the only
  // source of a stamp-less row with numbers is a backend older than this
  // sprint, and on THAT backend `usage` non-null did mean "fetched this
  // round". Muting it would assert staleness we have no evidence for, and
  // would blank the capacity on every surface during a deploy skew.
  //
  // Deliberately the opposite default from the backend's `usageRowAgeMs`
  // consumers (scheduler / broadcaster), which treat a missing stamp as
  // over-age. Different provenance: those read the in-process cache, where
  // a missing stamp can only be a bug in our own writer, and refusing to
  // score on it is right. This reads a wire payload from a peer that may
  // legitimately predate the field.
  if (atIso === null || atIso === undefined) return false;
  // UNPARSEABLE stamp is a different animal and gets the backend's answer.
  // A deploy skew produces an ABSENT field, never a corrupt one, so the
  // "older backend" justification above does not cover this case — the
  // only ways here are corruption or a broken writer, and neither earns
  // the benefit of the doubt. (Gate 2, 2026-08-12: the first version of
  // this helper collapsed the two through `usageAgeMs`'s single `null`.)
  const age = usageAgeMs(atIso, now);
  if (age === null) return true;
  return age > USAGE_UI_STALE_AFTER_MS;
}

// Sprint usage-last-known-good (D013-EC-172) D3 — the honest aggregate
// clock for a FLEET-level caption: the OLDEST reading on display. Any
// other choice (newest, or the store's entry-level `fetchedAtIso`) would
// caption a whole panel as fresher than its worst row, which is exactly
// the conflation the `atIso` rename exists to stop. Rows with no numbers
// do not participate — they render their own state, not an age.
//
// Lives beside the component rather than in either modal: all three
// founder-facing surfaces need it, and two of them are siblings.
export function oldestUsageAtIso(runners: RunnerUsageEntry[]): string | null {
  let oldest: string | null = null;
  let oldestMs = Number.POSITIVE_INFINITY;
  for (const r of runners) {
    if (!r.usage || !r.usageAtIso) continue;
    const ms = Date.parse(r.usageAtIso);
    if (!Number.isFinite(ms)) continue;
    if (ms < oldestMs) {
      oldestMs = ms;
      oldest = r.usageAtIso;
    }
  }
  return oldest;
}

// Gate 2 (2026-08-12) — should a FLEET-level caption be suppressed?
//
// True in exactly one case: rows are showing real numbers but none of
// them carries a stamp (a backend older than this sprint — deviation 3).
// Then `oldestUsageAtIso` is `null` and the caption would read "No usage
// reading yet" directly beneath live percentages.
//
// Deliberately NOT suppressed when no row has numbers at all: that is the
// genuine cold-cache case, and "No usage reading yet" is the true answer.
export function isFleetAgeCaptionSuppressed(runners: RunnerUsageEntry[]): boolean {
  return runners.some((r) => r.usage != null) && oldestUsageAtIso(runners) === null;
}

// Sprint usage-from-stream (spec D3b iii) — gates every RPC-SCOPED
// surface (`limits[]`, the per-model sonnet/opus bars, `extra_usage`):
// these fields are written ONLY by a successful RPC fetch and are never
// touched by a stream reading (spec §1 — the CLI stream carries zero
// per-model events), so they must render ONLY while the row's last RPC
// ATTEMPT actually succeeded AND that reading is not past the existing
// 60 min staleness bound. Deliberately does NOT derive its threshold
// from the scheduler's `USAGE_CACHE_MAX_SCHEDULER_AGE_MS` (same
// non-derivation note as `USAGE_UI_STALE_AFTER_MS` above — the two
// happen to share a number, nothing may derive one from the other).
//
// Why `lastAttempt === 'ok'` catches the founder's exact fear
// ("le queda 20 % real pero no se ha actualizado en 3 horas, pone
// 60 %"): a runner whose RPC keeps failing (the dark cohort this sprint
// fixes) gets `lastAttempt: 'upstream_error'` on EVERY warmer tick
// (~15 min), so this predicate goes false almost immediately — long
// before the merge's stream-fed `five_hour`/`seven_day` would make the
// ROW's own `usageAtIso` look fresh (D3's newest-stamp-among-buckets
// rule). Without this gate the modal would render an hours-old
// `limits[]` under a "hace 1 min" caption; with it, the per-model
// surfaces disappear and the muted `streamOnlyNote` (D4) explains why.
export function rpcBucketsUsable(
  entry: RunnerUsageEntry,
  now: number = Date.now(),
): boolean {
  if (!entry.usage) return false;
  // Same "absent ≠ failed" convention `RunnerLastAttemptNote` already
  // uses for a pre-D013-EC-172 backend (deploy-skew tolerance): an
  // OMITTED `lastAttempt` is not evidence of failure, only evidence the
  // wire predates the field. Only an EXPLICIT non-'ok' value disqualifies.
  if (entry.lastAttempt !== undefined && entry.lastAttempt !== 'ok') return false;
  return !isUsageReadingStale(entry.usageAtIso, now);
}

export function UsageAgeCaption({
  atIso,
  now,
  className,
  'data-testid': testId = 'usage-age-caption',
}: {
  // Sprint usage-last-known-good (D013-EC-172) D3 — renamed from
  // `fetchedAtIso`. The old name was the ENTRY-level clock (when the cache
  // last reached Anthropic for the fleet); every call site now passes the
  // PER-RUNNER `usageAtIso` (when these numbers were taken). The rename is
  // the point: the two clocks were being conflated at the call site, and a
  // conflated caption is a lie with a timestamp on it.
  atIso: string | null | undefined;
  now?: number;
  className?: string;
  'data-testid'?: string;
}) {
  const { t } = useTranslation();
  const resolved = usageAgeKey(usageAgeMs(atIso, now));

  const text =
    resolved.key === 'ageUnknown'
      ? t('usage.ageUnknown')
      : resolved.key === 'ageJustNow'
        ? t('usage.ageJustNow')
        : resolved.key === 'ageStale'
          ? t('usage.ageStale', { age: resolved.age })
          : t('usage.ageMinutes', { age: resolved.age });

  return (
    <span
      className={className ?? 'text-caption text-muted-foreground'}
      data-testid={testId}
      data-usage-age-state={resolved.key}
    >
      {text}
    </span>
  );
}
